// Sample data — Jordan Mercer / "Q2 AI Fluency Uplift" — for build parity and the
// "See a sample report" path. Bands only; no raw scores anywhere (matches the brief).
import type { AdminAnalytics, BudgetDto, ChatMessage, OrgSummary, PracticeScore, ReportView, SessionSummary, TemplateView } from './types';

export const sampleReport: ReportView = {
  assessmentId: 'sample-jordan-mercer',
  status: 'completed',
  name: 'Jordan Mercer',
  roleLine: 'Senior Presales Consultant · Go-to-Market · Assessed 12 Jun 2026',
  overallReadiness: { band: 'capable', label: 'Capable' },
  selfRatedBand: { band: 'developing', label: 'Developing' },
  confidence: 'high',
  velocity: 'medium',
  policyFit: 'at_risk',
  criteria: [
    { criterion: 'prompt_mastery', band: { band: 'capable', label: 'Capable' }, strength: 'You handle prompting confidently across familiar work.', nextStep: 'Add explicit role, context, task and output-format to one prompt this week.', evidenceQuotes: ['"I usually tell it who it is writing for first."', '"I iterate when the first answer is too generic."'], confidence: 'high' },
    { criterion: 'technical_understanding', band: { band: 'developing', label: 'Developing' }, strength: 'You apply AI to real tasks and are building reliable habits.', nextStep: 'Learn where models go wrong (context limits, confident errors) well enough to explain it.', evidenceQuotes: ['"I am not totally sure why it sometimes makes things up."'], confidence: 'medium' },
    { criterion: 'practical_application', band: { band: 'advanced', label: 'Advanced' }, strength: 'You bring real judgement here and others could learn from how you work.', nextStep: 'Templatise one task you already do with AI so it is repeatable for the team.', evidenceQuotes: ['"I rebuilt my call-prep routine around it and save an hour a week."', '"I use it for first-draft RFP responses."'], confidence: 'high' },
    { criterion: 'critical_evaluation', band: { band: 'capable', label: 'Capable' }, strength: 'You handle checking output confidently across familiar work.', nextStep: 'Build a quick verification step into one AI output you rely on before acting on it.', evidenceQuotes: ['"I always check figures against the source."'], confidence: 'medium' },
    { criterion: 'workflow_design', band: { band: 'developing', label: 'Developing' }, strength: 'You apply AI to parts of your process and are building habits.', nextStep: 'Map one end-to-end workflow and mark where AI helps and where a human gate is needed.', evidenceQuotes: ['"Handoffs to delivery are still mostly manual."'], confidence: 'medium' },
  ],
  fourD: [
    { dimension: 'description', status: 'strong' },
    { dimension: 'delegation', status: 'in_place' },
    { dimension: 'discernment', status: 'in_place' },
    { dimension: 'diligence', status: 'emerging' },
  ],
  primaryBottleneck: {
    key: 'verification',
    label: 'Verification habit',
    whatsOverProtected: 'You double-check everything manually, which slows you down on low-risk tasks.',
    recommendedLane: 'Reserve deep checking for high-stakes outputs; trust-but-spot-check the rest.',
    reframe: 'You are not under-careful — you are over-protecting low-risk work. Calibrate where the care goes.',
  },
  secondaryBottleneck: {
    key: 'workflow_integration',
    label: 'Workflow integration',
    whatsOverProtected: 'AI is used ad hoc rather than designed into the handoff to delivery.',
    recommendedLane: 'Design AI into one repeated handoff with a clear human gate.',
    reframe: 'Small structure here recovers the most time across the quarter.',
  },
  lanePrescription: 'Reserve deep checking for high-stakes outputs; trust-but-spot-check the rest.',
  policyViolations: [
    { finding: 'Pasted a draft client proposal into a non-approved consumer AI tool.', severity: 'high', recommendedAction: 'Move this work to the approved enterprise tool and confirm data-handling rules.' },
    { finding: 'Unclear what data class was involved in a summarisation task.', severity: 'medium', recommendedAction: 'Confirm the data class and apply the matching control before reusing.' },
    { finding: 'Occasional sharing of outputs without a source check.', severity: 'low', recommendedAction: 'Add a one-line provenance note when sharing AI-assisted work.' },
  ],
  policyViolationsCount: 3,
  workflowFindings: [
    { name: 'Call prep', observation: 'Rebuilt around AI; saves ~1 hour/week.', opportunity: 'Templatise the brief so the team can reuse it.' },
    { name: 'RFP drafting', observation: 'AI produces first drafts you then edit.', opportunity: 'Add an approved-tool gate and a source-check step.' },
    { name: 'Handoffs', observation: 'Still mostly manual into delivery.', opportunity: 'Design one structured, AI-assisted handoff.' },
  ],
  leverageSnapshot: {
    hoursRecoverable: 4.5,
    workflowsToTemplatise: 1,
    targetQuarter: 'Q3',
    targetBand: { band: 'advanced', label: 'Advanced' },
  },
  roadmap: [
    { window: 'Weeks 1–2', title: 'Calibrate verification', body: 'Decide what is high-stakes and only deep-check that.' },
    { window: 'Weeks 3–4', title: 'Templatise call prep', body: 'Turn your routine into a reusable team template.' },
    { window: 'Weeks 5–8', title: 'Design one handoff', body: 'Add AI with a clear human gate into the delivery handoff.' },
    { window: 'Weeks 9–12', title: 'Lift the team', body: 'Share the template so a colleague can run it without you.' },
  ],
  recommendedCertPath: {
    name: 'AI Fluency: Professional',
    focus: 'Repeatable workflows, delegation and discernment, and templatising what works.',
    cert: { name: 'Microsoft Certified: Azure AI Fundamentals', vendor: 'Microsoft', url: 'https://learn.microsoft.com/en-us/credentials/certifications/azure-ai-fundamentals/' },
  },
  immediateAction: { title: 'Draft first, edit second', body: 'For your next RFP response, let AI produce the first draft, then spend your time editing rather than starting from scratch.' },
  practiceDrills: [
    { title: 'Prompt structure', rubricLine: 'Role, context, task and output format all present.' },
    { title: 'Verification habit', rubricLine: 'You can name how you checked the output.' },
    { title: 'Workflow handoff', rubricLine: 'A colleague could run it from your notes.' },
  ],
  practiceMasterPrompt:
    'You are my prompt coach for presales work. Give me a realistic GTM task, let me draft a prompt, then critique it against role/context/task/output-format and show one improved version with what changed.',
  durationMins: 29,
  completedAt: '2026-06-12T03:30:00.000Z',
};

// Cohort-average bands for the "Compare to cohort" radar ghost.
export const sampleCohortRadar: Record<string, string> = {
  prompt_mastery: 'capable',
  technical_understanding: 'developing',
  practical_application: 'capable',
  critical_evaluation: 'developing',
  workflow_design: 'developing',
};

export const sampleAdmin: AdminAnalytics = {
  campaignId: 'q2-ai-fluency-uplift',
  kAnonymity: 5,
  kpis: {
    completionPct: 68,
    completeCount: 34,
    inProgressCount: 11,
    notStartedCount: 5,
    participantCount: 50,
    avgReadiness: { band: 'capable', label: 'Capable' },
  },
  capabilityRadar: [
    { criterion: 'prompt_mastery', band: { band: 'capable', label: 'Capable' } },
    { criterion: 'technical_understanding', band: { band: 'developing', label: 'Developing' } },
    { criterion: 'practical_application', band: { band: 'capable', label: 'Capable' } },
    { criterion: 'critical_evaluation', band: { band: 'developing', label: 'Developing' } },
    { criterion: 'workflow_design', band: { band: 'developing', label: 'Developing' } },
  ],
  fourDDistribution: [
    { dimension: 'description', strong: 18, in_place: 12, emerging: 4 },
    { dimension: 'delegation', strong: 10, in_place: 16, emerging: 8 },
    { dimension: 'discernment', strong: 9, in_place: 15, emerging: 10 },
    { dimension: 'diligence', strong: 6, in_place: 12, emerging: 16 },
  ],
  topBottlenecks: [
    { key: 'verification', label: 'Verification habit', pct: 38 },
    { key: 'workflow_integration', label: 'Workflow integration', pct: 29 },
    { key: 'prompt_specificity', label: 'Prompt specificity', pct: 18 },
    { key: 'trust_calibration', label: 'Trust calibration', pct: 15 },
  ],
  readinessByTeam: [
    { team: 'Go-to-Market', band: { band: 'capable', label: 'Capable' }, participantCount: 16, suppressed: false },
    { team: 'Delivery', band: { band: 'developing', label: 'Developing' }, participantCount: 14, suppressed: false },
    { team: 'Advisory', band: { band: 'capable', label: 'Capable' }, participantCount: 12, suppressed: false },
    { team: 'Operations', band: { band: 'developing', label: 'Developing' }, participantCount: 4, suppressed: true },
  ],
  policyRisk: { compliant: 28, at_risk: 14, non_compliant: 4 },
  roster: [
    { id: 'GTM-01', name: 'Priya Nair', email: 'priya.nair@example.com', role: 'Presales Consultant', team: 'Go-to-Market', status: 'completed', band: { band: 'capable', label: 'Capable' } },
    { id: 'GTM-07', name: 'Jordan Mercer', email: 'jordan.mercer@example.com', role: 'Senior Presales Consultant', team: 'Go-to-Market', status: 'completed', band: { band: 'advanced', label: 'Advanced' } },
    { id: 'DEL-03', name: 'Sam Okafor', email: 'sam.okafor@example.com', role: 'Delivery Lead', team: 'Delivery', status: 'in_progress' },
    { id: 'DEL-09', name: 'Lena Petrov', email: 'lena.petrov@example.com', role: 'Engineer', team: 'Delivery', status: 'completed', band: { band: 'developing', label: 'Developing' } },
    { id: 'ADV-02', name: 'Tomas Reyes', email: 'tomas.reyes@example.com', role: 'Advisor', team: 'Advisory', status: 'completed', band: { band: 'capable', label: 'Capable' } },
    { id: 'ADV-05', name: 'Mei Tanaka', email: 'mei.tanaka@example.com', role: 'Principal Advisor', team: 'Advisory', status: 'created' },
  ],
  suppressedTeamCount: 1,
  hiddenParticipantCount: 4,
};

// Illustrative campaign spend for the Budget screen in sample mode. Mirrors the backend
// BudgetDto (USD); the screen converts to AUD for display. Conversation + practice are
// the real cost lines (report is deterministic, voice is browser-side).
export const sampleBudget: BudgetDto = {
  campaignId: 'q2-ai-fluency-uplift',
  campaignName: 'Q2 AI Fluency Uplift',
  currency: 'USD',
  spentUsd: 124.8,
  projectedRemainingUsd: 37.2,
  headroomUsd: 88.0,
  budgetUsd: 250.0,
  breakdown: [
    { key: 'conversation', label: 'Conversation model', pct: 78, amountUsd: 97.34 },
    { key: 'practice', label: 'Practice drills', pct: 22, amountUsd: 27.46 },
    { key: 'report', label: 'Report synthesis', pct: 0, amountUsd: 0 },
    { key: 'voice', label: 'Voice transcription', pct: 0, amountUsd: 0 },
  ],
  rows: [
    { id: 'GTM-07', role: 'Senior Presales Consultant', team: 'Go-to-Market', durationMins: 31, tokens: 52400, costUsd: 3.42 },
    { id: 'ADV-05', role: 'Principal Advisor', team: 'Advisory', durationMins: 29, tokens: 48900, costUsd: 3.18 },
    { id: 'GTM-01', role: 'Presales Consultant', team: 'Go-to-Market', durationMins: 27, tokens: 44600, costUsd: 2.91 },
    { id: 'DEL-09', role: 'Engineer', team: 'Delivery', durationMins: 24, tokens: 40100, costUsd: 2.64 },
    { id: 'CS-03', role: 'Customer Success Lead', team: 'Customer Success', durationMins: 21, tokens: 33800, costUsd: 2.2 },
    { id: 'ADV-02', role: 'Advisor', team: 'Advisory', durationMins: 18, tokens: 30200, costUsd: 1.97 },
    { id: 'DEL-03', role: 'Delivery Lead', team: 'Delivery', durationMins: 14, tokens: 29700, costUsd: 1.95 },
    { id: 'MKT-06', role: 'Marketing Specialist', team: 'Marketing', durationMins: null, tokens: null, costUsd: null },
  ],
  rowsShown: 8,
  rowsStarted: 45,
  sampleSubtotalUsd: 18.27,
};

// Illustrative Organisations index for sample mode (the list screen). Versent is flagged
// internal. Selecting any row opens the detail screen, which renders sampleAdmin +
// sampleBudget (the deeper per-org sections are shared in the demo, as in the prototype).
export const sampleOrganisations: OrgSummary[] = [
  {
    campaignId: 'atlas', name: 'Atlas Financial Group', sector: 'Banking & financial services',
    status: 'active', periodStart: '2026-03-02', periodEnd: '2026-05-30',
    participantCount: 240, completeCount: 198, completionPct: 83,
    avgReadiness: { band: 'advanced', label: 'Advanced' }, spentUsd: 588.2, budgetUsd: 750,
  },
  {
    campaignId: 'meridian', name: 'Meridian Telecom', sector: 'Telecommunications',
    status: 'active', periodStart: '2026-03-09', periodEnd: '2026-06-09',
    participantCount: 120, completeCount: 82, completionPct: 68,
    avgReadiness: { band: 'capable', label: 'Capable' }, spentUsd: 310.4, budgetUsd: 600,
  },
  {
    campaignId: 'dot', name: 'Dept. of Transport', sector: 'Public sector',
    status: 'closed', periodStart: '2026-01-08', periodEnd: '2026-03-06',
    participantCount: 85, completeCount: 85, completionPct: 100,
    avgReadiness: { band: 'developing', label: 'Developing' }, spentUsd: 214.6, budgetUsd: 220,
  },
  {
    campaignId: 'q2-ai-fluency-uplift', name: 'Versent', sector: 'Internal · Technology consultancy',
    internal: true, status: 'active', periodStart: '2026-02-19', periodEnd: '2026-04-19',
    participantCount: 50, completeCount: 34, completionPct: 68,
    avgReadiness: { band: 'capable', label: 'Capable' }, spentUsd: 124.8, budgetUsd: 250,
  },
  {
    campaignId: 'northwind', name: 'Northwind Retail', sector: 'Retail & consumer',
    status: 'active', periodStart: '2026-04-01', periodEnd: '2026-07-01',
    participantCount: 64, completeCount: 21, completionPct: 33,
    avgReadiness: { band: 'developing', label: 'Developing' }, spentUsd: 61.3, budgetUsd: 200,
  },
];

// Canned practice-coach feedback for sample mode (no backend scoring).
export const samplePractice: PracticeScore = {
  score: 8,
  rubric: [
    { label: 'Role', met: true, note: 'You set who the AI should act as.' },
    { label: 'Context', met: true, note: 'Good background on the prospect and topic.' },
    { label: 'Task', met: true, note: 'The ask is clear.' },
    { label: 'Output format', met: false, note: 'Say how the answer should be shaped.' },
  ],
  improved:
    'You are a presales consultant. Context: a prospect in financial services asks how our platform handles data residency in ap-southeast-2. Task: write a one-page summary. Output: three short sections — what, where, controls — in plain English.',
  whatChanged: 'Added an explicit output format and the audience’s sector.',
};

// Scripted "My assessments" history for sample mode (no backend).
export const sampleHistory: SessionSummary[] = [
  { assessmentId: 'sample-jordan-mercer', status: 'completed', stage: 3, mode: 'quick', startedAt: '2026-06-12T03:01:00.000Z', lastActivityAt: '2026-06-12T03:30:00.000Z' },
  { assessmentId: 'sample-q1', status: 'completed', stage: 3, mode: 'quick', startedAt: '2026-03-04T22:10:00.000Z', lastActivityAt: '2026-03-04T22:41:00.000Z' },
  { assessmentId: 'sample-draft', status: 'in_progress', stage: 2, mode: 'conversational', startedAt: '2026-06-14T01:15:00.000Z', lastActivityAt: '2026-06-14T01:28:00.000Z' },
];

// A short scripted Avery exchange used when running without a live backend.
export const sampleOpening: ChatMessage = {
  id: 'open',
  role: 'avery',
  type: 'question',
  text: "Hi, I'm Avery. I'll ask about how you actually use AI in your work — there are no wrong answers, and we'll keep it to about half an hour. To start: think of a recent task where you used an AI tool. What were you trying to get done?",
};

export const sampleAveryReplies: string[] = [
  "That's a great example. When the first answer came back, what did you do next — did you take it as-is, or change something?",
  "Helpful. How do you know when an AI answer is good enough to use? Walk me through what you check.",
  "Thanks. Here's what I heard so far, tell me if I've got it wrong.",
  "Last one for this stage: which tool did you use for that, and was it an approved one?",
];

// Sample prompt-template library (sample mode only) — a Presales Consultant set plus a
// role-agnostic "general" template. Mirrors the model-generated shape.
export const sampleApprovedTools: string[] = ['Claude', 'Microsoft Copilot'];

export const sampleTemplates: TemplateView[] = [
  {
    templateId: 'tpl-presales-discovery',
    roleSlug: 'presales-consultant',
    roleLabel: 'Presales Consultant',
    title: 'Discovery call brief',
    summary: 'Turn messy call notes into a structured discovery brief.',
    fields: {
      role: 'You are a presales consultant preparing for a solution design.',
      context: 'Here are my raw notes from a discovery call: <paste notes>. The customer is in <industry> and cares about <priorities>.',
      task: 'Draft a discovery brief that captures their goals, current state, constraints and the open questions I still need answered.',
      outputFormat: 'Headed sections (Goals, Current state, Constraints, Open questions); bullet points; flag anything you inferred so I can confirm.',
    },
    prompt: 'Role: You are a presales consultant preparing for a solution design.\n\nContext: Here are my raw notes from a discovery call: <paste notes>. The customer is in <industry> and cares about <priorities>.\n\nTask: Draft a discovery brief that captures their goals, current state, constraints and the open questions I still need answered.\n\nOutput format: Headed sections (Goals, Current state, Constraints, Open questions); bullet points; flag anything you inferred so I can confirm.',
    source: 'generated',
    generatedAt: '2026-06-23T00:00:00Z',
  },
  {
    templateId: 'tpl-presales-objection',
    roleSlug: 'presales-consultant',
    roleLabel: 'Presales Consultant',
    title: 'Objection-handling prep',
    summary: 'Rehearse likely objections before a key meeting.',
    fields: {
      role: 'You are a sceptical buyer in <industry> evaluating our proposal.',
      context: 'Our proposal is <summary>. The buyer’s known concerns are <concerns>.',
      task: 'List the five toughest objections this buyer would raise, and a crisp, honest response to each.',
      outputFormat: 'A table: Objection | Why it matters to them | My response. Keep responses to two sentences.',
    },
    prompt: 'Role: You are a sceptical buyer in <industry> evaluating our proposal.\n\nContext: Our proposal is <summary>. The buyer’s known concerns are <concerns>.\n\nTask: List the five toughest objections this buyer would raise, and a crisp, honest response to each.\n\nOutput format: A table: Objection | Why it matters to them | My response. Keep responses to two sentences.',
    source: 'generated',
    generatedAt: '2026-06-23T00:00:00Z',
  },
  {
    templateId: 'tpl-general-draft',
    roleSlug: 'general',
    roleLabel: 'a professional',
    title: 'Draft and self-check',
    summary: 'Get a first draft you can edit rather than starting from a blank page.',
    fields: {
      role: 'You are a careful assistant who drafts, then flags anything you are unsure about.',
      context: 'Describe the audience, the goal, and any constraints or source material.',
      task: 'Produce a first draft of <the thing I need>.',
      outputFormat: 'A draft, followed by a short list of assumptions you made and anything I should verify.',
    },
    prompt: 'Role: You are a careful assistant who drafts, then flags anything you are unsure about.\n\nContext: Describe the audience, the goal, and any constraints or source material.\n\nTask: Produce a first draft of <the thing I need>.\n\nOutput format: A draft, followed by a short list of assumptions you made and anything I should verify.',
    source: 'generated',
    generatedAt: '2026-06-23T00:00:00Z',
  },
  {
    templateId: 'tpl-general-summarise',
    roleSlug: 'general',
    roleLabel: 'a professional',
    title: 'Summarise with sources',
    summary: 'Turn a long input into a decision-ready summary you can trust.',
    fields: {
      role: 'You are an analyst who summarises faithfully and never invents detail.',
      context: 'Paste the document or notes to summarise, and say who the summary is for.',
      task: 'Summarise the key points and the decisions or actions they imply.',
      outputFormat: 'Bullet points grouped by theme, with a one-line "what to check" note where the source is thin.',
    },
    prompt: 'Role: You are an analyst who summarises faithfully and never invents detail.\n\nContext: Paste the document or notes to summarise, and say who the summary is for.\n\nTask: Summarise the key points and the decisions or actions they imply.\n\nOutput format: Bullet points grouped by theme, with a one-line "what to check" note where the source is thin.',
    source: 'generated',
    generatedAt: '2026-06-23T00:00:00Z',
  },
  {
    templateId: 'tpl-general-compare',
    roleSlug: 'general',
    roleLabel: 'a professional',
    title: 'Compare the options',
    summary: 'Turn a fuzzy decision into a clear, side-by-side comparison.',
    fields: {
      role: 'You are an even-handed advisor who weighs trade-offs without pushing an agenda.',
      context: 'List the options you are weighing and what matters most in the decision.',
      task: 'Compare the options against the criteria that matter, and note the main trade-offs.',
      outputFormat: 'A comparison table, then a short recommendation with the one risk to watch.',
    },
    prompt: 'Role: You are an even-handed advisor who weighs trade-offs without pushing an agenda.\n\nContext: List the options you are weighing and what matters most in the decision.\n\nTask: Compare the options against the criteria that matter, and note the main trade-offs.\n\nOutput format: A comparison table, then a short recommendation with the one risk to watch.',
    source: 'generated',
    generatedAt: '2026-06-23T00:00:00Z',
  },
  {
    templateId: 'tpl-general-actions',
    roleSlug: 'general',
    roleLabel: 'a professional',
    title: 'Pull the actions from notes',
    summary: 'Convert raw meeting or call notes into clear owners and next steps.',
    fields: {
      role: 'You are a diligent note-taker who separates decisions from discussion.',
      context: 'Paste the meeting or call notes, and say who was involved.',
      task: 'Extract the decisions made and the action items, with an owner and a due date where stated.',
      outputFormat: 'A decisions list and an actions table (action, owner, due); flag any action with no clear owner.',
    },
    prompt: 'Role: You are a diligent note-taker who separates decisions from discussion.\n\nContext: Paste the meeting or call notes, and say who was involved.\n\nTask: Extract the decisions made and the action items, with an owner and a due date where stated.\n\nOutput format: A decisions list and an actions table (action, owner, due); flag any action with no clear owner.',
    source: 'generated',
    generatedAt: '2026-06-23T00:00:00Z',
  },
];
